import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUndo, faSearch } from "@fortawesome/free-solid-svg-icons";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext";
import "./LookupPatient.css";
import patientService from "../../services/patientService";
import practiceService from "../../services/practiceService";
import Heading from "../../components/Common/Heading/Heading";
import ListScreenSearchBar from "../../components/Common/ListScreenSearchBar/ListScreenSearchBar";
import ListScreenPaginationBar from "../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar";
import StatusBadge from "../../components/Common/StatusBadge/StatusBadge";
import Table from "../../components/Common/Table/Table";
import "../../components/Common/ListScreenPageLayout/listScreenPage.css";
import {
  buildPatientDetailsPath,
  resolvePatientPracticeId,
  resolvePatientRecordId
} from "../../utils/patientRouteUtils";

const lookupPatientResponseCache = new Map();

const LookupPatient = () => {
  const { user } = useSelector((state) => state.auth);
  const roleId = Number(user?.role_id);
  const isScopedRole = [4, 5, 7].includes(roleId); // Provider, Caregiver, System Caregiver
  const scopedPracticeId = useMemo(() => {
    const pid = user?.practice_id ?? user?.practiceId ?? user?.practices?.[0]?.id;
    return pid != null && pid !== "" ? String(pid) : "";
  }, [user?.practiceId, user?.practice_id, user?.practices]);
  const navigate = useNavigate();
  const { updateBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    const baseCrumb =
      roleId === 4
        ? { label: 'Patient Management', path: '/patient-management' }
        : roleId === 5
        ? { label: 'Caregiver Dashboard', path: '/caregiver-dashboard' }
        : roleId === 7
          ? { label: 'System Caregiver Dashboard', path: '/system-caregiver-dashboard' }
          : { label: 'Patient Management', path: '/patient-management' };
    updateBreadcrumbs([
      baseCrumb,
      { label: 'Look up Patient', path: null, active: true }
    ]);
  }, [roleId, updateBreadcrumbs]);

  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    practice: "",
    provider: "",
    caregiver: "",
    status: "",
    vitals: [],
    serialNumber: ""
  });

  const [patients, setPatients] = useState([]);
  const [allPatients, setAllPatients] = useState([]); // Store patients for current page
  const [tableLoading, setTableLoading] = useState(true);
  const [querySubmitting, setQuerySubmitting] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState("10");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [activePracticeId, setActivePracticeId] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    lastName: "",
    firstName: "",
    provider: "",
    caregiver: "",
    status: "",
    vitals: "",
    serialNumber: ""
  });
  const [sortConfig, setSortConfig] = useState({ key: 'rpmStartDate', direction: 'desc' });
  const hasInitializedRef = useRef(false);
  const hasInitialTableLoadRef = useRef(false);
  const isInitialTableLoadRef = useRef(false);
  const latestPatientsRequestRef = useRef(0);
  const queryFetchInProgressRef = useRef(false);
  const [hasQueryAttempted, setHasQueryAttempted] = useState(false);
  const cacheScopeKey = useMemo(
    () => `${roleId}:${isScopedRole ? scopedPracticeId || "scoped" : "global"}`,
    [roleId, isScopedRole, scopedPracticeId]
  );

  const isFiltersDirty = useMemo(() => {
    return (
      formData.lastName !== "" ||
      formData.firstName !== "" ||
      (!isScopedRole && formData.practice !== "") ||
      formData.provider !== "" ||
      formData.caregiver !== "" ||
      formData.status !== "" ||
      Array.isArray(formData.vitals) ? formData.vitals.length > 0 : formData.vitals !== "" ||
      formData.serialNumber !== ""
    );
  }, [formData, isScopedRole]);

  const effectivePracticeId = isScopedRole ? scopedPracticeId : (formData.practice || 'all');

  const apiLimit = useMemo(() => {
    if (entriesPerPage === "all" || entriesPerPage === "All") return 9999;
    const n = parseInt(entriesPerPage, 10);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, [entriesPerPage]);

  // Dropdown data
  const [practices, setPractices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [isVitalsDropdownOpen, setIsVitalsDropdownOpen] = useState(false);
  const vitalsDropdownRef = useRef(null);

  // Status options (matching old stack: 2=Active, 3=Pending, 4=Locked)
  const statusOptions = [
    { value: "2", label: "Active" },
    { value: "3", label: "Pending" },
    { value: "4", label: "Locked" }
  ];

  // Vitals options (matching old stack: 1=BP, 2=BG, 3=WT)
  const vitalsOptions = [
    { value: "1", label: "BP" },
    { value: "2", label: "BG" },
    { value: "3", label: "WT" }
  ];

  // Transform patient data to match expected format
  const transformPatientData = (patients) => {
    return patients.map(patient => {
      // Handle different field name variations
      const patientName = patient.patientName ||
        patient.patient_name ||
        (patient.first_name && patient.last_name ? `${patient.first_name} ${patient.last_name}` : '') ||
        patient.name || '';

      const address = patient.address ||
        patient.street_address ||
        (patient.address_line1 ? `${patient.address_line1}${patient.address_line2 ? ', ' + patient.address_line2 : ''}` : '') ||
        '';

      const phone = patient.phone ||
        patient.phone_number ||
        patient.phoneNumber || '';

      const email = patient.email || patient.email_address || '';

      const serial = patient.serial ||
        patient.serial_number ||
        patient.serialNumber || '';

      const rpmStartDateRaw = patient.rpmStartDate || patient.rpm_start_date || '';
      const rpmStartDate = patient.rpmStartDate ||
        patient.rpm_start_date ||
        patient.rpm_startDate ||
        (patient.rpm_start_date ? new Date(patient.rpm_start_date).toLocaleDateString() : '') ||
        '';

      // Transform status: 2=Active, 3=Pending, 4=Locked
      let status = patient.status || patient.status_id || '';
      if (typeof status === 'number' || (typeof status === 'string' && /^\d+$/.test(status))) {
        const statusMap = { '2': 'Active', '3': 'Pending', '4': 'Locked' };
        status = statusMap[status] || status;
      }

      // Transform vitals: 1=BP, 2=BG, 3=WT
      let vitals = patient.vitals || patient.vitals_id || '';
      if (typeof vitals === 'number' || (typeof vitals === 'string' && /^\d+$/.test(vitals))) {
        const vitalsMap = { '1': 'BP', '2': 'BG', '3': 'WT' };
        vitals = vitalsMap[vitals] || vitals;
      }

      const provider = patient.provider ||
        patient.provider_name ||
        (patient.provider_first_name && patient.provider_last_name ? `${patient.provider_first_name} ${patient.provider_last_name}` : '') ||
        '';

      const caregiver = patient.caregiver ||
        patient.caregiver_name ||
        (patient.caregiver_first_name && patient.caregiver_last_name ? `${patient.caregiver_first_name} ${patient.caregiver_last_name}` : '') ||
        '';

      return {
        ...patient,
        patient_table_id: patient.patient_table_id ?? patient.id,
        practice_id: patient.practice_id ?? patient.practiceId,
        patientName,
        address,
        phone,
        email,
        serial,
        rpmStartDate,
        rpmStartDateRaw,
        status,
        vitals,
        provider,
        caregiver
      };
    });
  };

  // Load initial data
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const initializeData = async () => {
      if (isScopedRole) {
        if (scopedPracticeId) {
          setFormData((prev) => ({ ...prev, practice: scopedPracticeId }));
          await loadProvidersAndCaregivers(scopedPracticeId);
        }
        return;
      }
      await loadPractices();
    };

    initializeData();
  }, [isScopedRole, scopedPracticeId, user]);

  // Page/size/filter changes after initial bootstrap (not on first open).
  useEffect(() => {
    if (!hasInitialTableLoadRef.current) return;
    if (!hasQueryAttempted) return;
    if (isInitialTableLoadRef.current) return;
    if (queryFetchInProgressRef.current) return;
    const targetPracticeId = activePracticeId;

    if (!targetPracticeId) return;

    if (isSearchMode) {
      fetchFilteredPatients(targetPracticeId, currentPage, apiLimit, { silent: false }, appliedFilters);
    } else {
      loadAllPatients(targetPracticeId, currentPage, apiLimit, { silent: false });
    }
  }, [apiLimit, currentPage, activePracticeId, isSearchMode, appliedFilters]);

  // Load providers and caregivers when practice changes
  useEffect(() => {
    const targetPracticeId = isScopedRole ? scopedPracticeId : formData.practice;
    if (targetPracticeId) {
      loadProvidersAndCaregivers(targetPracticeId);
    } else {
      setProviders([]);
      setCaregivers([]);
    }
  }, [formData.practice, isScopedRole, scopedPracticeId]);

  // Stop skeleton when scoped user has no practice to load.
  useEffect(() => {
    if (!isScopedRole) return;
    if (scopedPracticeId) return;
    if (!user) return;
    setTableLoading(false);
  }, [isScopedRole, scopedPracticeId, user]);

  // Initial table population: skeleton until first page is fetched.
  useEffect(() => {
    const targetPracticeId = effectivePracticeId;
    if (!targetPracticeId) return;
    if (hasInitialTableLoadRef.current) return;
    if (hasQueryAttempted) return;

    hasInitialTableLoadRef.current = true;
    isInitialTableLoadRef.current = true;
    setTableLoading(true);
    setIsSearchMode(false);
    setCurrentPage(1);
    setActivePracticeId(targetPracticeId);

    (async () => {
      try {
        await loadAllPatients(targetPracticeId, 1, apiLimit, { silent: true });
        setHasQueryAttempted(true);
      } finally {
        setTableLoading(false);
        isInitialTableLoadRef.current = false;
      }
    })();
  }, [effectivePracticeId, apiLimit, hasQueryAttempted]);

  const loadPractices = async () => {
    try {
      setLoadingDropdowns(true);
      const response = await practiceService.getPractices({ limit: 1000, page: 1 });

      // Handle different response structures
      let practicesData = [];
      if (response && response.data) {
        if (response.data.success) {
          if (response.data.data) {
            if (Array.isArray(response.data.data)) {
              practicesData = response.data.data;
            } else if (response.data.data.practices && Array.isArray(response.data.data.practices)) {
              practicesData = response.data.data.practices;
            } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
              practicesData = response.data.data.data;
            }
          }
        } else if (Array.isArray(response.data)) {
          practicesData = response.data;
        } else if (response.data.practices && Array.isArray(response.data.practices)) {
          practicesData = response.data.practices;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          practicesData = response.data.data;
        }
      }

      // Remove duplicates based on ID
      const uniquePractices = practicesData.filter((practice, index, self) =>
        index === self.findIndex((p) => p.id === practice.id)
      );

      // Ensure it's always an array
      setPractices(Array.isArray(uniquePractices) ? uniquePractices : []);
    } catch (error) {
      console.error("Error loading practices:", error);
      toast.error("Failed to load practices");
      setPractices([]); // Ensure it's an array even on error
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const loadProvidersAndCaregivers = async (practiceId) => {
    try {
      setLoadingDropdowns(true);
      const [providersRes, caregiversRes] = await Promise.all([
        practiceService.getPracticeProviders(practiceId),
        practiceService.getPracticeCaregivers(practiceId)
      ]);

      // Handle providers - check multiple response structures
      let providersData = [];
      if (providersRes && providersRes.data) {
        if (providersRes.data.success) {
          if (providersRes.data.data) {
            if (Array.isArray(providersRes.data.data)) {
              providersData = providersRes.data.data;
            } else if (providersRes.data.data.providers && Array.isArray(providersRes.data.data.providers)) {
              providersData = providersRes.data.data.providers;
            }
          }
        } else if (Array.isArray(providersRes.data)) {
          providersData = providersRes.data;
        } else if (providersRes.data.providers && Array.isArray(providersRes.data.providers)) {
          providersData = providersRes.data.providers;
        } else if (providersRes.data.data && Array.isArray(providersRes.data.data)) {
          providersData = providersRes.data.data;
        }
      }

      // Remove duplicates based on ID
      const uniqueProviders = providersData.filter((provider, index, self) =>
        index === self.findIndex((p) => p.id === provider.id)
      );
      setProviders(Array.isArray(uniqueProviders) ? uniqueProviders : []);

      // Handle caregivers - check multiple response structures
      let caregiversData = [];
      if (caregiversRes && caregiversRes.data) {
        if (caregiversRes.data.success) {
          if (caregiversRes.data.data) {
            if (Array.isArray(caregiversRes.data.data)) {
              caregiversData = caregiversRes.data.data;
            } else if (caregiversRes.data.data.caregivers && Array.isArray(caregiversRes.data.data.caregivers)) {
              caregiversData = caregiversRes.data.data.caregivers;
            }
          }
        } else if (Array.isArray(caregiversRes.data)) {
          caregiversData = caregiversRes.data;
        } else if (caregiversRes.data.caregivers && Array.isArray(caregiversRes.data.caregivers)) {
          caregiversData = caregiversRes.data.caregivers;
        } else if (caregiversRes.data.data && Array.isArray(caregiversRes.data.data)) {
          caregiversData = caregiversRes.data.data;
        }
      }

      // Remove duplicates based on ID
      const uniqueCaregivers = caregiversData.filter((caregiver, index, self) =>
        index === self.findIndex((c) => c.id === caregiver.id)
      );
      setCaregivers(Array.isArray(uniqueCaregivers) ? uniqueCaregivers : []);
    } catch (error) {
      console.error("Error loading providers/caregivers:", error);
      toast.error("Failed to load providers/caregivers");
      setProviders([]);
      setCaregivers([]);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // Generic Sorting Function
  const sortPatients = (patientsList) => {
    let sortedPatients = [...patientsList];

    const getStatusOrder = (status) => {
      if (!status) return 99;
      const statusStr = status.toString().toUpperCase();
      if (statusStr === '2' || statusStr === 'ACTIVE' || statusStr === 'A') return 1;
      if (statusStr === '3' || statusStr === 'PENDING' || statusStr === 'P') return 2;
      if (statusStr === '4' || statusStr === 'LOCKED' || statusStr === 'L') return 3;
      return 99;
    };

    sortedPatients.sort((a, b) => {
      // If user explicitly sorts by RPM Start Date, sort only by RPM date.
      if (sortConfig.key === 'rpmStartDate') {
        const rawA = a.rpmStartDateRaw;
        const rawB = b.rpmStartDateRaw;
        const dateA = rawA ? new Date(rawA) : null;
        const dateB = rawB ? new Date(rawB) : null;

        const aValue = dateA && !Number.isNaN(dateA.getTime()) ? dateA.getTime() : null;
        const bValue = dateB && !Number.isNaN(dateB.getTime()) ? dateB.getTime() : null;

        if (aValue == null && bValue == null) {
          return String(a.patientName || '').localeCompare(String(b.patientName || ''), undefined, {
            sensitivity: 'base'
          });
        }
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;

        return String(a.patientName || '').localeCompare(String(b.patientName || ''), undefined, {
          sensitivity: 'base'
        });
      }

      // 1. Primary Sort: Status Priority
      const statusA = getStatusOrder(a.status);
      const statusB = getStatusOrder(b.status);
      
      if (statusA !== statusB) {
        return statusA - statusB;
      }

      // 2. Secondary Sort: Based on sortConfig (default is rpmStartDate desc)
      if (sortConfig.key) {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Specific handling for dates
        if (sortConfig.key === 'rpmStartDate') {
          const rawValue = a.rpmStartDateRaw;
          const rawValueB = b.rpmStartDateRaw;
          const dateA = rawValue ? new Date(rawValue) : null;
          const dateB = rawValueB ? new Date(rawValueB) : null;
          
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1; // Put nulls at bottom
          if (!dateB) return -1;

          aValue = dateA.getTime();
          bValue = dateB.getTime();
        }
        else if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
      }

      // 3. Tertiary Sort: Name (if status and sort key are same)
      const nameA = (a.patientName || "").toLowerCase();
      const nameB = (b.patientName || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return sortedPatients;
  };

  // Always apply status-priority ordering first: Active -> Pending -> Locked
  const applyStatusPrioritySort = (patientsList) => {
    const getStatusOrder = (status) => {
      if (!status) return 99;
      const statusStr = status.toString().toUpperCase();
      if (statusStr === '2' || statusStr === 'ACTIVE' || statusStr === 'A') return 1;
      if (statusStr === '3' || statusStr === 'PENDING' || statusStr === 'P') return 2;
      if (statusStr === '4' || statusStr === 'LOCKED' || statusStr === 'L') return 3;
      return 99;
    };
    return [...patientsList].sort((a, b) => getStatusOrder(a.status) - getStatusOrder(b.status));
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Load patients for a practice with server-side pagination
  const loadAllPatients = async (practiceId, page = 1, limit = 30, options = {}) => {
    const { silent = false, showToast = false } = options;
    if (!practiceId) return;
    const requestId = ++latestPatientsRequestRef.current;
    const cacheKey = JSON.stringify({
      scope: cacheScopeKey,
      mode: "all",
      practiceId: String(practiceId),
      page,
      limit
    });

    if (!silent) setTableLoading(true);
    try {
      const cached = lookupPatientResponseCache.get(cacheKey);
      if (cached) {
        if (requestId !== latestPatientsRequestRef.current) return;
        setPatients(cached.patients);
        setAllPatients(cached.patients);
        setTotalPatients(cached.total);
        return;
      }

      const response = (practiceId === 'all')
        ? await patientService.lookupPatient('all', { page, limit })
        : await patientService.getPatients(practiceId, { page, limit });

      let patientsData = [];
      let pagination = { total: 0 };
      const payload = response?.data;
      if (payload) {
        if (payload?.success && Array.isArray(payload?.data?.patients)) {
          patientsData = payload.data.patients;
        } else if (payload?.success && Array.isArray(payload?.data?.data)) {
          patientsData = payload.data.data;
        } else if (payload?.success && Array.isArray(payload?.data)) {
          patientsData = payload.data;
        } else if (Array.isArray(payload?.patients)) {
          patientsData = payload.patients;
        } else if (Array.isArray(payload?.data)) {
          patientsData = payload.data;
        }
        pagination = payload?.data?.pagination || payload?.pagination || { total: 0 };
      }

      const transformedPatients = transformPatientData(Array.isArray(patientsData) ? patientsData : []);
      const statusSortedPatients = applyStatusPrioritySort(transformedPatients);
      if (requestId !== latestPatientsRequestRef.current) return;
      setPatients(statusSortedPatients);
      setAllPatients(statusSortedPatients);
      setTotalPatients(pagination.total || statusSortedPatients.length);
      lookupPatientResponseCache.set(cacheKey, {
        patients: statusSortedPatients,
        total: pagination.total || statusSortedPatients.length
      });
    } catch (error) {
      console.error("Error loading patients:", error);
      if (requestId !== latestPatientsRequestRef.current) return;
      setPatients([]);
      setAllPatients([]);
      setTotalPatients(0);
      if (showToast) {
        toast.error("Failed to load patients", { toastId: "lookup-patient-load-error" });
      }
    } finally {
      if (!silent) setTableLoading(false);
    }
  };

  const fetchFilteredPatients = async (practiceId, page = 1, limit = 30, options = {}, filterOverride = null) => {
    const { silent = false, showToast = false } = options;
    if (!practiceId) return;
    const requestId = ++latestPatientsRequestRef.current;

    if (!silent) {
      setTableLoading(true);
      // Prevent stale rows from flashing while new query is in progress.
      setPatients([]);
      setAllPatients([]);
      setTotalPatients(0);
    }
    try {
      const sourceFilters = filterOverride || {
        lastName: formData.lastName,
        firstName: formData.firstName,
        provider: formData.provider,
        caregiver: formData.caregiver,
        status: formData.status,
        vitals: Array.isArray(formData.vitals) ? formData.vitals.join(",") : formData.vitals,
        serialNumber: formData.serialNumber
      };
      const filters = {
        lastName: sourceFilters.lastName || "",
        firstName: sourceFilters.firstName || "",
        provider: sourceFilters.provider || "",
        caregiver: sourceFilters.caregiver || "",
        status: sourceFilters.status || "",
        vitals: sourceFilters.vitals || "",
        serialNumber: sourceFilters.serialNumber || "",
        page,
        limit
      };
      const cacheKey = JSON.stringify({
        scope: cacheScopeKey,
        mode: "filtered",
        practiceId: String(practiceId),
        page,
        limit,
        filters: {
          lastName: filters.lastName || "",
          firstName: filters.firstName || "",
          provider: filters.provider || "",
          caregiver: filters.caregiver || "",
          status: filters.status || "",
          vitals: filters.vitals || "",
          serialNumber: filters.serialNumber || ""
        }
      });
      const cached = lookupPatientResponseCache.get(cacheKey);
      if (cached) {
        if (requestId !== latestPatientsRequestRef.current) return;
        setPatients(cached.patients);
        setAllPatients(cached.patients);
        setTotalPatients(cached.total);
        return;
      }

      const response = await patientService.lookupPatient(practiceId, filters);

      let patientsData = [];
      let pagination = { total: 0 };
      const payload = response?.data;
      if (payload) {
        if (payload?.success && Array.isArray(payload?.data?.patients)) {
          patientsData = payload.data.patients;
        } else if (payload?.success && Array.isArray(payload?.data?.data)) {
          patientsData = payload.data.data;
        } else if (payload?.success && Array.isArray(payload?.data)) {
          patientsData = payload.data;
        } else if (Array.isArray(payload?.patients)) {
          patientsData = payload.patients;
        } else if (Array.isArray(payload?.data)) {
          patientsData = payload.data;
        }
        pagination = payload?.data?.pagination || payload?.pagination || { total: 0 };
      }

      const transformedPatients = transformPatientData(Array.isArray(patientsData) ? patientsData : []);
      const statusSortedPatients = applyStatusPrioritySort(transformedPatients);
      if (requestId !== latestPatientsRequestRef.current) return;
      setPatients(statusSortedPatients);
      setAllPatients(statusSortedPatients);
      setTotalPatients(pagination.total || statusSortedPatients.length);
      lookupPatientResponseCache.set(cacheKey, {
        patients: statusSortedPatients,
        total: pagination.total || statusSortedPatients.length
      });

    } catch (error) {
      console.error("Error fetching filtered patients:", error);
      if (requestId !== latestPatientsRequestRef.current) return;
      if (showToast) {
        toast.error("Request failed", { toastId: "lookup-patient-query-error" });
      }
      setPatients([]);
      setAllPatients([]);
      setTotalPatients(0);
    } finally {
      if (!silent) setTableLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === "serialNumber") {
      nextValue = String(value).replace(/\D/g, "").slice(0, 16);
    }
    if (name === "practice") {
      setFormData((prev) => ({ ...prev, practice: nextValue, provider: "", caregiver: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const toggleVitalOption = (value) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.vitals) ? prev.vitals : [];
      const exists = current.includes(value);
      return {
        ...prev,
        vitals: exists ? current.filter((v) => v !== value) : [...current, value]
      };
    });
  };

  const handleReset = () => {
    setFormData({
      lastName: "",
      firstName: "",
      practice: isScopedRole ? scopedPracticeId : "",
      provider: "",
      caregiver: "",
      status: "",
      vitals: [],
      serialNumber: ""
    });
    setPatients([]);
    setAllPatients([]);
    setSearchTerm("");
    setHasQueryAttempted(false);
    setTotalPatients(0);
    setCurrentPage(1);
    setIsSearchMode(false);
    setActivePracticeId("");
    setAppliedFilters({
      lastName: "",
      firstName: "",
      provider: "",
      caregiver: "",
      status: "",
      vitals: "",
      serialNumber: ""
    });
    hasInitialTableLoadRef.current = false;
    setIsVitalsDropdownOpen(false);
  };

  useEffect(() => {
    const onClickOutside = (event) => {
      if (vitalsDropdownRef.current && !vitalsDropdownRef.current.contains(event.target)) {
        setIsVitalsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);



  const handleQuery = async (e) => {
    e.preventDefault();

    const practiceId = effectivePracticeId;
    if (!practiceId) {
      toast.error("Practice is required");
      return;
    }

    setIsSearchMode(true);
    setHasQueryAttempted(true);
    setActivePracticeId(practiceId);
    const nextAppliedFilters = {
      lastName: formData.lastName || "",
      firstName: formData.firstName || "",
      provider: formData.provider || "",
      caregiver: formData.caregiver || "",
      status: formData.status || "",
      vitals: Array.isArray(formData.vitals) ? formData.vitals.join(",") : (formData.vitals || ""),
      serialNumber: formData.serialNumber || ""
    };
    setAppliedFilters(nextAppliedFilters);
    queryFetchInProgressRef.current = true;
    setQuerySubmitting(true);
    try {
      await fetchFilteredPatients(practiceId, 1, apiLimit, { silent: false }, nextAppliedFilters);
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } finally {
      setQuerySubmitting(false);
      queryFetchInProgressRef.current = false;
    }
  };

  // Filter patients by search term
  const filteredPatients = patients.filter((patient) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      patient.patientName?.toLowerCase().includes(searchLower) ||
      patient.email?.toLowerCase().includes(searchLower) ||
      patient.phone?.toLowerCase().includes(searchLower) ||
      patient.serial?.toLowerCase().includes(searchLower) ||
      patient.address?.toLowerCase().includes(searchLower)
    );
  });

  // Sort filtered patients
  const sortedAndFilteredPatients = sortPatients(filteredPatients);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(totalPatients / apiLimit) || 1);

  // Ensure currentPage is within bounds [1, totalPages]
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    } else if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const paginatedPatients = sortedAndFilteredPatients;
  const selectedVitalLabels = useMemo(
    () =>
      vitalsOptions
        .filter((option) =>
          String(appliedFilters.vitals || "")
            .split(",")
            .map((v) => v.trim())
            .includes(option.value)
        )
        .map((option) => option.label),
    [appliedFilters.vitals]
  );

  const handlePageChange = (newPage) => {
    const validatedPage = Math.max(1, Math.min(newPage, totalPages));
    if (validatedPage !== currentPage) {
      setCurrentPage(validatedPage);
    }
  };

  const handlePatientRowClick = (patient) => {
    const patientId = resolvePatientRecordId(patient);
    const practiceIdForRoute = resolvePatientPracticeId(
      patient,
      activePracticeId || effectivePracticeId || (isScopedRole ? scopedPracticeId : formData.practice)
    );
    const path = buildPatientDetailsPath(practiceIdForRoute, patientId);
    if (!path) {
      toast.error("Unable to open patient details. Select a practice or use a patient with a practice assigned.");
      return;
    }
    navigate(path);
  };

  const columns = [
    {
      key: "index",
      title: "#",
      sortable: false,
      className: "lookup-patient-index-col reusable-table-centered-col",
      render: (_patient, index) => (currentPage - 1) * apiLimit + index + 1
    },
    {
      key: "status",
      title: "Status",
      sortable: true,
      className: "lookup-patient-status-col reusable-table-centered-col",
      render: (patient) => <StatusBadge status={patient.status} />
    },
    {
      key: "vitals",
      title: "Vitals",
      sortable: true,
      className: "lookup-patient-vitals-col",
      render: (patient) => {
        const value = String(patient.vitals || "").trim();
        if (!value) return "-";
        if (selectedVitalLabels.length === 0) return value;
        const patientVitals = value
          .split(",")
          .map((v) => v.trim().toUpperCase())
          .filter(Boolean);
        const filtered = selectedVitalLabels.filter((label) => patientVitals.includes(label.toUpperCase()));
        return filtered.length > 0 ? filtered.join(", ") : "-";
      }
    },
    { key: "patientName", title: "Patient Name", sortable: true, render: (patient) => patient.patientName || "-" },
    { key: "address", title: "Address", sortable: true, className: "lookup-patient-address-col", render: (patient) => patient.address || "-" },
    { key: "phone", title: "Phone number", sortable: true, className: "lookup-patient-phone-col", render: (patient) => patient.phone || "-" },
    { key: "email", title: "Email", sortable: true, className: "lookup-patient-email-col", render: (patient) => patient.email || "-" },
    { key: "serial", title: "Serial #", sortable: true, className: "lookup-patient-serial-col", render: (patient) => patient.serial || "-" },
    { key: "rpmStartDate", title: "RPM start date", sortable: true, render: (patient) => patient.rpmStartDate || "-" },
    { key: "provider", title: "Provider", sortable: true, className: "lookup-patient-provider-col", render: (patient) => patient.provider || "-" },
    { key: "caregiver", title: "Caregiver", sortable: true, className: "lookup-patient-caregiver-col", render: (patient) => patient.caregiver || "-" }
  ];

  return (
    <div className="list-screen-page-container lookup-patient-page">
      <div className="list-screen-top-bar">
        <div className="list-screen-title-section">
          <Heading title="Look up Patient" level="h2" className="list-screen-title-text" />
        </div>
        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={searchTerm}
            onChange={(val) => {
              setSearchTerm(val);
              setCurrentPage(1);
            }}
            inputId="lookup-patient-search"
          />
        </div>
      </div>

      <div className="lookup-patient-card lookup-patient-filter-card">
        <form className="lookup-patient-form" onSubmit={handleQuery}>
          <div className="lookup-patient-form-grid">
            <div className="lookup-patient-form-group">
              <label>Last Name</label>
              <input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="lookup-patient-input"
                type="text"
                placeholder=""
              />
            </div>

            <div className="lookup-patient-form-group">
              <label>First Name</label>
              <input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="lookup-patient-input"
                type="text"
                placeholder=""
              />
            </div>

            {!isScopedRole && (
              <div className="lookup-patient-form-group">
                <label>Practice</label>
                <select
                  name="practice"
                  value={formData.practice}
                  onChange={handleChange}
                  className="lookup-patient-select"
                  disabled={loadingDropdowns}
                >
                  <option value="">-- Select --</option>
                  {Array.isArray(practices) && practices.map((practice, index) => (
                    <option key={practice.id || `practice-${index}`} value={practice.id}>
                      {practice.practice_name || practice.name || 'Unnamed Practice'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="lookup-patient-form-group">
              <label>Provider</label>
              <select
                name="provider"
                value={formData.provider}
                onChange={handleChange}
                className="lookup-patient-select"
                disabled={!effectivePracticeId || loadingDropdowns}
              >
                <option value="">-- Select --</option>
                {Array.isArray(providers) && providers.map((provider, index) => (
                  <option key={provider.id || `provider-${index}`} value={provider.id}>
                    {provider.last_name}, {provider.first_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="lookup-patient-form-group">
              <label>Caregiver</label>
              <select
                name="caregiver"
                value={formData.caregiver}
                onChange={handleChange}
                className="lookup-patient-select"
                disabled={!effectivePracticeId || loadingDropdowns}
              >
                <option value="">-- Select --</option>
                {Array.isArray(caregivers) && caregivers.map((caregiver, index) => (
                  <option key={caregiver.id || `caregiver-${index}`} value={caregiver.id}>
                    {caregiver.last_name}, {caregiver.first_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="lookup-patient-form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="lookup-patient-select"
              >
                <option value="">-- Select --</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="lookup-patient-form-group">
              <label>Vitals</label>
              <div className="lookup-patient-multiselect" ref={vitalsDropdownRef}>
                <button
                  type="button"
                  className="lookup-patient-multiselect-trigger"
                  onClick={() => setIsVitalsDropdownOpen((prev) => !prev)}
                >
                  <span>
                    {Array.isArray(formData.vitals) && formData.vitals.length > 0
                      ? vitalsOptions
                          .filter((option) => formData.vitals.includes(option.value))
                          .map((option) => option.label)
                          .join(", ")
                      : "-- Select --"}
                  </span>
                  <span className="lookup-patient-multiselect-arrow">{isVitalsDropdownOpen ? "▲" : "▼"}</span>
                </button>
                {isVitalsDropdownOpen && (
                  <div className="lookup-patient-multiselect-menu">
                    {vitalsOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`lookup-patient-multiselect-option${
                          Array.isArray(formData.vitals) && formData.vitals.includes(option.value) ? " selected" : ""
                        }`}
                        onClick={() => toggleVitalOption(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="lookup-patient-form-group">
              <label>Serial #</label>
              <input
                name="serialNumber"
                value={formData.serialNumber}
                onChange={handleChange}
                className="lookup-patient-input"
                type="text"
                inputMode="numeric"
                maxLength={16}
                placeholder="16 digits"
                title="Enter exactly 16 digits"
                aria-label="Serial number, 16 digits"
              />
            </div>
          </div>

          <div className="lookup-patient-form-actions">
            <button
              type="button"
              className="reset-btn"
              onClick={handleReset}
              disabled={!isFiltersDirty}
              title={!isFiltersDirty ? "No filters selected" : ""}
            >
              <FontAwesomeIcon icon={faUndo} /> Reset
            </button>
            <button
              type="submit"
              className="query-btn"
              disabled={querySubmitting}
            >
              <FontAwesomeIcon icon={faSearch} /> {querySubmitting ? "Querying..." : "Query"}
            </button>
          </div>
        </form>

        <div className="lookup-patient-table-section">
          <div className="lookup-patient-table-wrapper">
            <Table
              columns={columns}
              data={paginatedPatients}
              sortBy={sortConfig.key}
              sortOrder={sortConfig.direction}
              onSort={requestSort}
              isLoading={tableLoading || querySubmitting}
              loadingMode="skeleton"
              skeletonRows={8}
              emptyMessage="No patients found"
              onRowClick={handlePatientRowClick}
              getRowKey={(patient, index) =>
                `${resolvePatientPracticeId(patient, activePracticeId) || "p"}-${resolvePatientRecordId(patient) || index}`
              }
            />
          </div>

          {totalPatients > 0 && (
            <ListScreenPaginationBar
              entriesValue={String(entriesPerPage)}
              onEntriesChange={(value) => {
                const nextEntries = value === "all" ? "all" : String(value);
                setEntriesPerPage(nextEntries);
                setCurrentPage(1);
              }}
              showPageSelector={entriesPerPage !== "all" && entriesPerPage !== "All"}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LookupPatient;
